import React from "react";
import { Link } from "react-router-dom";
import RoboticsShell from "./RoboticsShell";
import { getBrand } from "../../config/brandIdentity";

/**
 * RoboticsColophon — /robotics/about.
 *
 * Permanent attribution + content-transparency + copyright page. Linked
 * from the Robotics footer. Kept intentionally text-heavy and quiet —
 * this is a legal / trust page, not a marketing surface.
 */
export default function RoboticsColophon() {
  const brand = getBrand();
  return (
    <RoboticsShell soft>
      <main className="rbx-page rbx-colophon" data-testid="rbx-colophon-page">
        <div className="rbx-colophon__eyebrow" data-testid="rbx-colophon-eyebrow">
          FTG · ROBOTICS ACADEMY
        </div>
        <div className="rbx-colophon__formal" data-testid="rbx-colophon-formal" style={{ fontFamily: "var(--rbx-font-display)", color: "var(--rbx-gold)", fontSize: 18, marginBottom: 8 }}>
          {brand.formal}
        </div>
        <div style={{ color: "var(--rbx-ink-mute)", fontSize: 13, marginBottom: 20 }} data-testid="rbx-colophon-trading">
          {brand.trading}
        </div>
        <h1 className="rbx-colophon__title" data-testid="rbx-colophon-title">
          About &amp; Colophon
        </h1>

        <section className="rbx-colophon__section" data-testid="rbx-colophon-promise">
          <h2>Our promise</h2>
          <p><em>Wherever you are. Whoever you are.</em></p>
          <p>
            One thing binds us all: the desire to explore this new
            AI-driven world we&rsquo;re building together.
          </p>
          <p>
            There is a genius inside every child. Your quest is to bring
            that genius out &mdash; and release it to the new world.
          </p>
          <p>
            We are here to facilitate that quest. From the United Kingdom,
            <strong> Qualification Evaluation UK Ltd.</strong> &mdash; trading
            as <strong>FutureTec Robotics Academy</strong> &mdash; fulfils
            the dream of every genius boy and every genius girl, wherever
            they live.
          </p>
        </section>

        <section className="rbx-colophon__section" data-testid="rbx-colophon-mission">
          <h2>About FutureTec Robotics Academy</h2>
          <p>
            FutureTec Robotics Academy exists to teach young people — ages
            eight to seventeen — how machines actually work, from the very
            first LED all the way up to the servo joints of a walking robot.
            The curriculum is engineered from first principles: every idea
            is grounded in physics, mathematics and gentle hands-on
            experiment before it is dressed up in code. We do not sell
            hobby kits. We do not sell answers. We teach children how to
            reason about the world so they can build their own.
          </p>
          <p>
            A parallel <Link to="/robotics/girls-at-tec" data-testid="rbx-colophon-girls-link">Girls@TEC</Link>
            {" "}pathway runs through every band, ensuring that engineering is
            spoken to girls in the same voice, at the same depth, from day
            one. And every drama, every lesson, every diagram is offered in
            the tradition of Sir Arthur C. Clarke — the gentleman scientist
            who, from his study in Colombo, believed that any curious child,
            anywhere, could build the future if someone would just take the
            time to show them the physics.
          </p>
        </section>

        <section className="rbx-colophon__section" data-testid="rbx-colophon-attribution">
          <h2>Attribution</h2>
          <p>
            Curriculum directed by <strong>Niranjan Nanayakkara Gamage</strong>,
            Founder of FutureTec Robotics Academy — organiser of the
            first LEGO Robotics Olympiad in Sri Lanka, 2004, personally
            supported by the late <strong>Sir Arthur C. Clarke</strong>.
          </p>
        </section>

        <section className="rbx-colophon__section" data-testid="rbx-colophon-transparency">
          <h2>Content transparency</h2>
          <p>
            Lesson content is AI-authored and human-directed. Each lesson,
            drama, and diagram is designed, sequenced, and reviewed for
            pedagogical soundness by the FutureTec team. Where a lesson
            covers a scientific claim, we cite the primary source; where
            it uses a simplification, we say so out loud. If you spot a
            mistake or a place where our explanation could be sharper, we
            want to hear from you — write to us via the {" "}
            <Link to="/robotics/schools" data-testid="rbx-colophon-schools-link">
              Schools page
            </Link>.
          </p>
        </section>

        <section className="rbx-colophon__section" data-testid="rbx-colophon-copyright">
          <h2>Copyright</h2>
          <p>
            &copy; 2026 Qualification Evaluation UK Ltd. FutureTec Robotics
            Academy is a trading name of Qualification Evaluation UK Ltd.,
            registered in the United Kingdom.
          </p>
          <p style={{ color: "var(--rbx-ink-mute)", fontSize: 13.5 }}>
            The Clarke Legacy dramas &mdash; text, audio, and paintings &mdash;
            are the work of the FutureTec Robotics Academy team. Sir Arthur C.
            Clarke&rsquo;s name is used in respectful biographical context; the
            Clarke estate is not a party to this curriculum.
          </p>
        </section>
      </main>
    </RoboticsShell>
  );
}
